export interface HouseholdLearner {
  id: number;
  name: string;
  email?: string;
  phone?: string;
  admissionNumber?: string | null;
  classId?: number | null;
  className?: string | null;
}

const HOUSEHOLD_LEARNERS_KEY = "householdLearners";
const ACTIVE_LEARNER_ID_KEY = "activeLearnerId";
const AUTH_USER_ID_KEY = "authUserId";
const ACTIVE_LEARNER_EVENT = "active-learner-changed";

const safeJsonParse = <T,>(value: string | null, fallback: T): T => {
  if (!value) return fallback;
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
};

export const getStoredHouseholdLearners = (): HouseholdLearner[] => {
  return safeJsonParse<HouseholdLearner[]>(localStorage.getItem(HOUSEHOLD_LEARNERS_KEY), []);
};

export const getStoredActiveLearnerId = (): number | null => {
  const raw = localStorage.getItem(ACTIVE_LEARNER_ID_KEY) || localStorage.getItem("userId");
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

export const getStoredActiveLearner = (): HouseholdLearner | null => {
  const learners = getStoredHouseholdLearners();
  const activeId = getStoredActiveLearnerId();
  return learners.find((learner) => learner.id === activeId) || learners[0] || null;
};

const dispatchLearnerChange = () => {
  window.dispatchEvent(new CustomEvent(ACTIVE_LEARNER_EVENT));
};

export const syncActiveLearner = (learnerId?: number | null) => {
  const learners = getStoredHouseholdLearners();
  const nextLearner =
    learners.find((learner) => learner.id === Number(learnerId)) ||
    learners[0] ||
    null;

  if (!nextLearner) {
    localStorage.removeItem(ACTIVE_LEARNER_ID_KEY);
    dispatchLearnerChange();
    return null;
  }

  localStorage.setItem(ACTIVE_LEARNER_ID_KEY, String(nextLearner.id));
  localStorage.setItem("userId", String(nextLearner.id));
  localStorage.setItem("userName", nextLearner.name || "");
  localStorage.setItem("email", nextLearner.email || "");
  dispatchLearnerChange();
  return nextLearner;
};

export const persistStudentSession = (authenticatedUserId: number, learners: HouseholdLearner[]) => {
  localStorage.setItem(AUTH_USER_ID_KEY, String(authenticatedUserId));
  localStorage.setItem(HOUSEHOLD_LEARNERS_KEY, JSON.stringify(learners || []));
  return syncActiveLearner(getStoredActiveLearnerId() || learners[0]?.id || authenticatedUserId);
};

export const clearStudentSession = () => {
  localStorage.removeItem(HOUSEHOLD_LEARNERS_KEY);
  localStorage.removeItem(ACTIVE_LEARNER_ID_KEY);
  localStorage.removeItem(AUTH_USER_ID_KEY);
};

export const getStudentAuthHeaders = (token?: string | null) => {
  const headers: Record<string, string> = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  const role = (localStorage.getItem("role") || "").toLowerCase();
  const activeLearnerId = getStoredActiveLearnerId();
  if (role === "student" && activeLearnerId) {
    headers["X-Active-Student-Id"] = String(activeLearnerId);
  }
  return headers;
};

export const onActiveLearnerChange = (callback: () => void) => {
  window.addEventListener(ACTIVE_LEARNER_EVENT, callback);
  window.addEventListener("storage", callback);
  return () => {
    window.removeEventListener(ACTIVE_LEARNER_EVENT, callback);
    window.removeEventListener("storage", callback);
  };
};
